import { computed } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';

export type ProgressType = 'idle' | 'starting' | 'loading' | 'completing';

interface LoadingState {
  loading: boolean;
  progress: ProgressType;
  error: string | null;
  activeRequests: number;
}

const initialState: LoadingState = {
  loading: false,
  progress: 'idle',
  error: null,
  activeRequests: 0,
};

export const LoadingStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(({ loading, error, progress, activeRequests }) => ({
    isLoading: computed(() => loading()),
    currentError: computed(() => error()),
    currentProgress: computed(() => progress()),
    hasActiveRequests: computed(() => activeRequests() > 0),
    progressValue: computed(() => {
      switch (progress()) {
        case 'starting':
          return 0;
        case 'loading':
          return 80;
        case 'completing':
          return 100;
        default:
          return 0;
      }
    }),
    progressMode: computed(() => {
      return progress() === 'loading' ? 'determinate' : 'indeterminate';
    }),
    loadingMessageKey: computed(() => {
      switch (progress()) {
        case 'starting':
          return 'common.loading.initializing';
        case 'loading':
          return 'common.loading.loadingData';
        case 'completing':
          return 'common.loading.finalizing';
        default:
          return 'common.loading.default';
      }
    }),
  })),
  withMethods(store => ({
    startRequest() {
      patchState(
        store,
        (state): Partial<LoadingState> => ({
          loading: true,
          activeRequests: state.activeRequests + 1,
          progress: 'starting',
          error: null,
        }),
      );
    },
    updateProgress(progress: ProgressType) {
      patchState(store, { progress });
    },
    completeRequest(error: string | null = null) {
      patchState(store, state => {
        const newActiveRequests = Math.max(0, state.activeRequests - 1);
        return {
          loading: newActiveRequests > 0,
          activeRequests: newActiveRequests,
          progress: newActiveRequests > 0 ? state.progress : 'idle',
          error,
        };
      });
    },
  })),
  withMethods(store => ({
    startLoading() {
      patchState(store, { loading: true, error: null });
    },
    stopLoading(error: string | null = null) {
      patchState(store, { loading: false, error });
    },
    setLoading(isLoading: boolean) {
      patchState(store, { loading: isLoading });
    },
    setError(error: string | null) {
      patchState(store, { error });
    },
    stopLoadingAndSetError(error: string) {
      patchState(store, { loading: false, error });
    },
  })),
);
